import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { 
  Settings, MessageSquare, Database, BarChart, Copy, Check, 
  Trash2, Plus, Save, ArrowLeft, Loader2, Crown, CreditCard, ExternalLink,
  Shield, Globe, X, AlertTriangle, Bell, Eye
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";

interface Anomaly {
  id: string;
  anomaly_type: string;
  severity: string;
  session_id: string | null;
  ip_hash: string | null;
  details: Record<string, any>;
  acknowledged: boolean;
  created_at: string;
}

interface BlockedIP {
  id: string;
  ip_hash: string;
  reason: string;
  blocked_at: string;
  expires_at: string | null;
  is_active: boolean;
  anomaly_count: number;
}

// Stripe Price IDs
const STRIPE_PRICES = {
  basic: "price_1RTyVIE6AAfMiC13F6cqxD6A",
  pro: "price_1RTyWJE6AAfMiC13dkY7KJom",
  premium: "price_1RTyX3E6AAfMiC13CBWF8vVu"
};

const SUPABASE_URL = "https://ydwqhnwvmsghmfdbgwub.supabase.co";

interface TenantData {
  tenant: {
    id: string;
    email: string;
    companyName: string;
    contactName: string;
    plan: string;
    isActive: boolean;
    createdAt: string;
    allowedDomains: string[] | null;
  };
  config: {
    bot_name: string;
    welcome_message: string;
    primary_color: string;
    secondary_color: string;
    logo_url: string;
    contact_email: string;
    contact_phone: string;
    whatsapp_number: string;
    system_prompt: string;
    show_klartech_branding: boolean;
    callback_enabled: boolean;
  };
  knowledge: Array<{
    id: string;
    category: string;
    title: string;
    content: string;
    priority: number;
    is_active: boolean;
  }>;
  usage: {
    today: { messages: number; conversations: number };
    month: { messages: number; conversations: number };
  };
}

const PLAN_LIMITS = {
  free: { daily: 10, monthly: null, customKnowledge: false, customBranding: false },
  basic: { daily: null, monthly: 150, customKnowledge: false, customBranding: false },
  pro: { daily: null, monthly: 750, customKnowledge: true, customBranding: true },
  premium: { daily: null, monthly: 2000, customKnowledge: true, customBranding: true },
};

export default function ChatbotAdmin() {
  const [apiKey, setApiKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TenantData | null>(null);
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState<TenantData["config"] | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState({ category: "faq", title: "", content: "" });
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState<string | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState("");
  const [isSavingDomains, setIsSavingDomains] = useState(false);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [isLoadingAnomalies, setIsLoadingAnomalies] = useState(false);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [isLoadingBlockedIPs, setIsLoadingBlockedIPs] = useState(false);
  const { toast } = useToast();

  // Check for stored API key
  useEffect(() => {
    const storedKey = localStorage.getItem("kt_chatbot_api_key");
    if (storedKey) {
      setApiKey(storedKey);
      loadTenantData(storedKey);
    }
  }, []);

  const loadTenantData = async (key: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-get-tenant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: key })
      });

      const result = await response.json();

      if (response.ok) {
        setData(result);
        setConfig(result.config);
        setAllowedDomains(result.tenant.allowedDomains || []);
        localStorage.setItem("kt_chatbot_api_key", key);
      } else {
        toast({
          title: "Fehler",
          description: result.error || "API-Key nicht gefunden",
          variant: "destructive"
        });
        localStorage.removeItem("kt_chatbot_api_key");
      }
    } catch (error) {
      toast({
        title: "Verbindungsfehler",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setIsSaving(true);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-update-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey, config })
      });

      const result = await response.json();

      if (response.ok) {
        toast({ title: "Erfolgreich", description: "Einstellungen wurden gespeichert." });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Fehler beim Speichern",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addKnowledge = async () => {
    if (!newKnowledge.title || !newKnowledge.content) {
      toast({ title: "Fehler", description: "Titel und Inhalt sind erforderlich.", variant: "destructive" });
      return;
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-manage-knowledge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          apiKey, 
          action: "create", 
          knowledge: newKnowledge 
        })
      });

      const result = await response.json();

      if (response.ok) {
        toast({ title: "Erfolgreich", description: "Wissensbeitrag wurde hinzugefügt." });
        setNewKnowledge({ category: "faq", title: "", content: "" });
        loadTenantData(apiKey); // Refresh
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    }
  };

  const deleteKnowledge = async (id: string) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-manage-knowledge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey, action: "delete", knowledge: { id } })
      });

      if (response.ok) {
        toast({ title: "Erfolgreich", description: "Wissensbeitrag wurde gelöscht." });
        loadTenantData(apiKey);
      }
    } catch (error) {
      toast({ title: "Fehler", description: "Löschen fehlgeschlagen.", variant: "destructive" });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check subscription status
  const checkSubscription = async () => {
    if (!apiKey) return;
    setIsCheckingSubscription(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-check-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey })
      });

      if (response.ok) {
        // Reload tenant data to get updated plan
        await loadTenantData(apiKey);
        toast({ title: "Erfolgreich", description: "Abonnement-Status wurde aktualisiert." });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Status konnte nicht abgerufen werden.",
        variant: "destructive"
      });
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Start checkout for upgrade
  const handleUpgrade = async (plan: "basic" | "pro" | "premium") => {
    setIsUpgrading(plan);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey, priceId: STRIPE_PRICES[plan] })
      });

      const result = await response.json();

      if (response.ok && result.url) {
        window.open(result.url, "_blank");
      } else {
        throw new Error(result.error || "Checkout konnte nicht erstellt werden");
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsUpgrading(null);
    }
  };

  // Open customer portal
  const openCustomerPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-customer-portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey })
      });

      const result = await response.json();

      if (response.ok && result.url) {
        window.open(result.url, "_blank");
      } else {
        throw new Error(result.error || "Portal konnte nicht geöffnet werden");
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  // Save allowed domains
  const saveDomains = async () => {
    setIsSavingDomains(true);
    try {
      const { data: result, error } = await supabase.functions.invoke(
        "chatbot-update-domains",
        { body: { apiKey, allowedDomains } }
      );

      if (error) {
        throw error;
      }

      toast({ title: "Erfolgreich", description: (result as any)?.message || "Gespeichert." });
    } catch (error) {
      toast({
        title: "Fehler beim Speichern",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSavingDomains(false);
    }
  };

  // Add a new domain
  const addDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    
    // Basic validation
    const domainRegex = /^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
    if (!domainRegex.test(domain)) {
      toast({
        title: "Ungültige Domain",
        description: "Bitte geben Sie eine gültige Domain ein (z.B. example.com oder *.example.com)",
        variant: "destructive"
      });
      return;
    }
    
    if (allowedDomains.includes(domain)) {
      toast({
        title: "Domain existiert bereits",
        description: "Diese Domain ist bereits in der Liste.",
        variant: "destructive"
      });
      return;
    }
    
    setAllowedDomains([...allowedDomains, domain]);
    setNewDomain("");
  };

  // Remove a domain
  const removeDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domain));
  };

  // Load anomalies
  const loadAnomalies = async () => {
    if (!apiKey) return;
    setIsLoadingAnomalies(true);
    try {
      const { data: result, error } = await supabase.functions.invoke(
        "chatbot-get-anomalies",
        { body: { apiKey } }
      );

      if (error) {
        throw error;
      }

      setAnomalies((result as any)?.anomalies || []);
    } catch (error) {
      console.error("Failed to load anomalies:", error);
    } finally {
      setIsLoadingAnomalies(false);
    }
  };

  // Acknowledge an anomaly
  const acknowledgeAnomaly = async (anomalyId: string) => {
    try {
      const { error } = await supabase.functions.invoke(
        "chatbot-acknowledge-anomaly",
        { body: { apiKey, anomalyId } }
      );

      if (error) {
        throw error;
      }

      setAnomalies(anomalies.map(a =>
        a.id === anomalyId ? { ...a, acknowledged: true } : a
      ));
      toast({ title: "Erfolgreich", description: "Warnung wurde bestätigt." });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Warnung konnte nicht bestätigt werden.",
        variant: "destructive"
      });
    }
  };

  // Load blocked IPs
  const loadBlockedIPs = async () => {
    if (!apiKey) return;
    setIsLoadingBlockedIPs(true);
    try {
      const { data: result, error } = await supabase.functions.invoke(
        "chatbot-get-blocked-ips",
        { body: { apiKey } }
      );

      if (error) {
        throw error;
      }

      setBlockedIPs((result as any)?.blockedIPs || []);
    } catch (error) {
      console.error("Failed to load blocked IPs:", error);
    } finally {
      setIsLoadingBlockedIPs(false);
    }
  };

  // Unblock an IP
  const unblockIP = async (blockId: string) => {
    try {
      const { error } = await supabase.functions.invoke(
        "chatbot-unblock-ip",
        { body: { apiKey, blockId } }
      );

      if (error) {
        throw error;
      }

      setBlockedIPs(blockedIPs.map(b =>
        b.id === blockId ? { ...b, is_active: false } : b
      ));
      toast({ title: "Erfolgreich", description: "IP-Blockierung wurde aufgehoben." });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "IP konnte nicht entsperrt werden.",
        variant: "destructive"
      });
    }
  };

  // Format anomaly type
  const formatAnomalyType = (type: string) => {
    switch (type) {
      case "high_rate_session": return "Hohe Session-Rate";
      case "high_rate_ip": return "Hohe IP-Rate";
      case "suspicious_pattern": return "Verdächtiges Muster";
      case "ip_blocked": return "IP blockiert";
      default: return type;
    }
  };

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "destructive";
      case "warning": return "secondary";
      default: return "outline";
    }
  };

  const embedCode = `<script src="https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-widget-js" data-api-key="${apiKey}"></script>`;

  const plan = data?.tenant.plan as keyof typeof PLAN_LIMITS || "free";
  const limits = PLAN_LIMITS[plan];
  
  const unacknowledgedAnomalies = anomalies.filter(a => !a.acknowledged).length;
  const activeBlockedIPs = blockedIPs.filter(b => b.is_active && (!b.expires_at || new Date(b.expires_at) > new Date())).length;

  // Login view
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>ChatBot Dashboard</CardTitle>
            <CardDescription>
              Geben Sie Ihren API-Key ein, um auf Ihr Dashboard zuzugreifen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="kt_chat_..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <Button 
              className="w-full" 
              onClick={() => loadTenantData(apiKey)}
              disabled={isLoading || !apiKey}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Dashboard öffnen
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Noch keinen API-Key?{" "}
              <Link to="/chatbot-produkt" className="text-primary underline">
                Jetzt registrieren
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-bold text-lg">ChatBot Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                {data.tenant.companyName || data.tenant.contactName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              plan === "premium" ? "bg-yellow-100 text-yellow-800" :
              plan === "pro" ? "bg-blue-100 text-blue-800" :
              plan === "basic" ? "bg-green-100 text-green-800" :
              "bg-gray-100 text-gray-800"
            }`}>
              {plan.charAt(0).toUpperCase() + plan.slice(1)}
            </span>
            {plan !== "free" && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openCustomerPortal}
                disabled={isOpeningPortal}
              >
                {isOpeningPortal ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
                Abo verwalten
              </Button>
            )}
            {plan !== "premium" && (
              <Button variant="default" size="sm" onClick={() => handleUpgrade(plan === "free" ? "basic" : plan === "basic" ? "pro" : "premium")} disabled={isUpgrading !== null}>
                {isUpgrading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Crown className="w-4 h-4 mr-1" />}
                Upgrade
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart className="w-4 h-4" /> Übersicht
            </TabsTrigger>
            <TabsTrigger value="config" className="gap-2">
              <Settings className="w-4 h-4" /> Einstellungen
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" onClick={() => { loadAnomalies(); loadBlockedIPs(); }}>
              <Shield className="w-4 h-4" /> 
              Sicherheit
              {(unacknowledgedAnomalies > 0 || activeBlockedIPs > 0) && (
                <span className="ml-1 bg-destructive text-destructive-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                  {unacknowledgedAnomalies + activeBlockedIPs}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="gap-2" disabled={!limits.customKnowledge}>
              <Database className="w-4 h-4" /> Wissensdatenbank
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2">
              <CreditCard className="w-4 h-4" /> Abonnement
            </TabsTrigger>
            <TabsTrigger value="embed" className="gap-2">
              <MessageSquare className="w-4 h-4" /> Einbettung
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Nachrichten heute</p>
                  <p className="text-3xl font-bold">
                    {data.usage.today.messages}
                    {limits.daily && (
                      <span className="text-lg font-normal text-muted-foreground">
                        /{limits.daily}
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Nachrichten diesen Monat</p>
                  <p className="text-3xl font-bold">
                    {data.usage.month.messages}
                    {limits.monthly && (
                      <span className="text-lg font-normal text-muted-foreground">
                        /{limits.monthly}
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Gespräche heute</p>
                  <p className="text-3xl font-bold">{data.usage.today.conversations}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Gespräche diesen Monat</p>
                  <p className="text-3xl font-bold">{data.usage.month.conversations}</p>
                </CardContent>
              </Card>
            </div>

            {/* Usage bar */}
            {limits.monthly && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="text-lg">Monatliches Kontingent</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all"
                      style={{ width: `${Math.min((data.usage.month.messages / limits.monthly) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {data.usage.month.messages} von {limits.monthly} Nachrichten verwendet 
                    ({Math.round((data.usage.month.messages / limits.monthly) * 100)}%)
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            {config && (
              <Card>
                <CardHeader>
                  <CardTitle>Bot-Einstellungen</CardTitle>
                  <CardDescription>
                    Passen Sie Ihren Chatbot an Ihre Bedürfnisse an.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Bot-Name</label>
                      <Input
                        value={config.bot_name}
                        onChange={(e) => setConfig({ ...config, bot_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Kontakt-E-Mail</label>
                      <Input
                        value={config.contact_email || ""}
                        onChange={(e) => setConfig({ ...config, contact_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Telefon</label>
                      <Input
                        value={config.contact_phone || ""}
                        onChange={(e) => setConfig({ ...config, contact_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">WhatsApp</label>
                      <Input
                        value={config.whatsapp_number || ""}
                        onChange={(e) => setConfig({ ...config, whatsapp_number: e.target.value })}
                        placeholder="+49..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Begrüßungsnachricht</label>
                    <Textarea
                      value={config.welcome_message}
                      onChange={(e) => setConfig({ ...config, welcome_message: e.target.value })}
                      rows={2}
                    />
                  </div>

                  {limits.customBranding && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Primärfarbe</label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.primary_color || "#0066cc"}
                              onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                              className="w-16 h-10 p-1"
                            />
                            <Input
                              value={config.primary_color || "#0066cc"}
                              onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Logo-URL</label>
                          <Input
                            value={config.logo_url || ""}
                            onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium">System-Prompt (KI-Anweisungen)</label>
                        <Textarea
                          value={config.system_prompt || ""}
                          onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
                          rows={4}
                          placeholder="Du bist ein freundlicher Assistent für..."
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Klartech Branding ausblenden</p>
                          <p className="text-sm text-muted-foreground">
                            "Powered by Klartech" wird nicht mehr angezeigt
                          </p>
                        </div>
                        <Switch
                          checked={!config.show_klartech_branding}
                          onCheckedChange={(checked) => setConfig({ ...config, show_klartech_branding: !checked })}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Rückruf-Funktion</p>
                      <p className="text-sm text-muted-foreground">
                        Besucher können einen Rückruf anfordern
                      </p>
                    </div>
                    <Switch
                      checked={config.callback_enabled}
                      onCheckedChange={(checked) => setConfig({ ...config, callback_enabled: checked })}
                    />
                  </div>

                  <Button onClick={saveConfig} disabled={isSaving} className="gap-2">
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Einstellungen speichern
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Domain-Validierung
                </CardTitle>
                <CardDescription>
                  Beschränken Sie die Nutzung Ihres API-Keys auf bestimmte Domains. 
                  Wenn keine Domains eingetragen sind, funktioniert der API-Key überall.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current domains list */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Erlaubte Domains</label>
                  {allowedDomains.length === 0 ? (
                    <div className="text-muted-foreground text-sm p-4 border rounded-lg bg-muted/50 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Alle Domains erlaubt (keine Einschränkung)
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {allowedDomains.map((domain) => (
                        <div 
                          key={domain} 
                          className="flex items-center justify-between p-3 border rounded-lg bg-card"
                        >
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <code className="text-sm">{domain}</code>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeDomain(domain)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add new domain */}
                <div className="flex gap-2">
                  <Input
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="z.B. example.com oder *.example.com"
                    onKeyDown={(e) => e.key === "Enter" && addDomain()}
                  />
                  <Button onClick={addDomain} variant="outline" className="gap-2 shrink-0">
                    <Plus className="w-4 h-4" /> Hinzufügen
                  </Button>
                </div>

                {/* Info box */}
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Wie funktioniert die Domain-Validierung?</h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>• Der API-Key funktioniert nur auf den eingetragenen Domains</li>
                    <li>• <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">*.example.com</code> erlaubt alle Subdomains</li>
                    <li>• <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">www</code>-Varianten werden automatisch mit erlaubt</li>
                    <li>• Ohne eingetragene Domains ist der API-Key unrestriktiert</li>
                  </ul>
                </div>

                {/* Save button */}
                <Button onClick={saveDomains} disabled={isSavingDomains} className="gap-2">
                  {isSavingDomains ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Domain-Einstellungen speichern
                </Button>
              </CardContent>
            </Card>

            {/* Anomaly Detection Card */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Anomalie-Erkennung
                    </CardTitle>
                    <CardDescription>
                      Verdächtige Aktivitäten und potenzielle Missbrauchsversuche
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadAnomalies} disabled={isLoadingAnomalies}>
                    {isLoadingAnomalies ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aktualisieren"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingAnomalies ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : anomalies.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Anomalien in den letzten 7 Tagen erkannt.</p>
                    <p className="text-sm">Das System überwacht automatisch ungewöhnliche Aktivitäten.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {anomalies.map((anomaly) => (
                      <div 
                        key={anomaly.id} 
                        className={`flex items-start justify-between p-4 border rounded-lg ${
                          anomaly.acknowledged 
                            ? "bg-muted/30 border-muted" 
                            : anomaly.severity === "critical"
                              ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                              : "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800"
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className={`w-4 h-4 ${
                              anomaly.acknowledged 
                                ? "text-muted-foreground" 
                                : anomaly.severity === "critical" 
                                  ? "text-red-600" 
                                  : "text-yellow-600"
                            }`} />
                            <span className="font-medium">{formatAnomalyType(anomaly.anomaly_type)}</span>
                            <Badge variant={getSeverityColor(anomaly.severity) as any}>
                              {anomaly.severity === "critical" ? "Kritisch" : "Warnung"}
                            </Badge>
                            {anomaly.acknowledged && (
                              <Badge variant="outline">Bestätigt</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {anomaly.session_id && (
                              <p>Session: <code className="bg-muted px-1 rounded">{anomaly.session_id.slice(0, 20)}...</code></p>
                            )}
                            {anomaly.ip_hash && (
                              <p>IP-Hash: <code className="bg-muted px-1 rounded">{anomaly.ip_hash}</code></p>
                            )}
                            {anomaly.details?.requestsPerMinute && (
                              <p>Requests/Minute: {anomaly.details.requestsPerMinute} (Limit: {anomaly.details.threshold})</p>
                            )}
                            {anomaly.details?.requestsPerHour && (
                              <p>Requests/Stunde: {anomaly.details.requestsPerHour} (Limit: {anomaly.details.threshold})</p>
                            )}
                            {anomaly.details?.uniqueSessions && (
                              <p>Unique Sessions: {anomaly.details.uniqueSessions} (Limit: {anomaly.details.threshold})</p>
                            )}
                            {anomaly.details?.possibleBot && (
                              <p className="text-red-600 dark:text-red-400 font-medium">⚠️ Möglicher Bot erkannt</p>
                            )}
                            <p className="text-xs">
                              Erkannt: {new Date(anomaly.created_at).toLocaleString("de-DE")}
                            </p>
                          </div>
                        </div>
                        {!anomaly.acknowledged && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => acknowledgeAnomaly(anomaly.id)}
                            className="shrink-0"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Bestätigen
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Info about anomaly detection */}
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">Was wird überwacht?</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Session-Rate:</strong> Mehr als 15 Requests/Minute pro Session</li>
                    <li>• <strong>IP-Rate:</strong> Mehr als 100 Requests/Stunde pro IP</li>
                    <li>• <strong>Bot-Erkennung:</strong> Mehr als 20 verschiedene Sessions von einer IP</li>
                    <li>• <strong>Auto-Blockierung:</strong> Nach 3 kritischen Anomalien wird die IP für 24h blockiert</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Blocked IPs Card */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Blockierte IPs
                      {activeBlockedIPs > 0 && (
                        <Badge variant="destructive">{activeBlockedIPs} aktiv</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      IPs die aufgrund wiederholter Anomalien automatisch blockiert wurden
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadBlockedIPs} disabled={isLoadingBlockedIPs}>
                    {isLoadingBlockedIPs ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aktualisieren"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingBlockedIPs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : blockedIPs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Keine blockierten IPs.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blockedIPs.map((block) => {
                      const isExpired = block.expires_at && new Date(block.expires_at) < new Date();
                      const isActive = block.is_active && !isExpired;
                      
                      return (
                        <div 
                          key={block.id} 
                          className={`flex items-start justify-between p-4 border rounded-lg ${
                            isActive
                              ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                              : "bg-muted/30 border-muted"
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <code className="text-sm font-mono">{block.ip_hash}</code>
                              {isActive ? (
                                <Badge variant="destructive">Blockiert</Badge>
                              ) : isExpired ? (
                                <Badge variant="outline">Abgelaufen</Badge>
                              ) : (
                                <Badge variant="outline">Aufgehoben</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{block.reason}</p>
                            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                              <p>Blockiert: {new Date(block.blocked_at).toLocaleString("de-DE")}</p>
                              {block.expires_at && (
                                <p>Läuft ab: {new Date(block.expires_at).toLocaleString("de-DE")}</p>
                              )}
                              <p>Anomalien: {block.anomaly_count}</p>
                            </div>
                          </div>
                          {isActive && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => unblockIP(block.id)}
                              className="shrink-0"
                            >
                              Entsperren
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Knowledge Tab */}
          <TabsContent value="knowledge">
            {!limits.customKnowledge ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">Wissensdatenbank nicht verfügbar</h3>
                  <p className="text-muted-foreground mb-4">
                    Die Wissensdatenbank ist ab dem Pro-Plan verfügbar.
                  </p>
                  <Button asChild>
                    <Link to="/chatbot-produkt#pricing">Jetzt upgraden</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Neuen Eintrag hinzufügen</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Kategorie</label>
                        <Input
                          value={newKnowledge.category}
                          onChange={(e) => setNewKnowledge({ ...newKnowledge, category: e.target.value })}
                          placeholder="z.B. faq, services, preise"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Titel</label>
                        <Input
                          value={newKnowledge.title}
                          onChange={(e) => setNewKnowledge({ ...newKnowledge, title: e.target.value })}
                          placeholder="z.B. Öffnungszeiten"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Inhalt</label>
                      <Textarea
                        value={newKnowledge.content}
                        onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
                        rows={3}
                        placeholder="Beschreiben Sie den Inhalt, den der Bot kennen soll..."
                      />
                    </div>
                    <Button onClick={addKnowledge} className="gap-2">
                      <Plus className="w-4 h-4" /> Hinzufügen
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Vorhandene Einträge</CardTitle>
                    <CardDescription>
                      {data.knowledge.length} Einträge in der Wissensdatenbank
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {data.knowledge.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        Noch keine Einträge vorhanden.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {data.knowledge.map((item) => (
                          <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg">
                            <div>
                              <span className="text-xs bg-muted px-2 py-0.5 rounded">
                                {item.category}
                              </span>
                              <h4 className="font-medium mt-1">{item.title}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {item.content}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteKnowledge(item.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Plan */}
              <Card>
                <CardHeader>
                  <CardTitle>Ihr aktuelles Abonnement</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Plan:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      plan === "premium" ? "bg-yellow-100 text-yellow-800" :
                      plan === "pro" ? "bg-blue-100 text-blue-800" :
                      plan === "basic" ? "bg-green-100 text-green-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </span>
                  </div>
                  
                  {limits.monthly && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Monatliches Limit:</span>
                      <span>{limits.monthly} Nachrichten</span>
                    </div>
                  )}
                  
                  {limits.daily && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Tägliches Limit:</span>
                      <span>{limits.daily} Nachrichten</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Eigenes Branding:</span>
                    <span>{limits.customBranding ? "✓ Ja" : "✗ Nein"}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Wissensdatenbank:</span>
                    <span>{limits.customKnowledge ? "✓ Ja" : "✗ Nein"}</span>
                  </div>

                  <div className="pt-4 space-y-2">
                    <Button 
                      onClick={checkSubscription} 
                      variant="outline" 
                      className="w-full"
                      disabled={isCheckingSubscription}
                    >
                      {isCheckingSubscription ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Status aktualisieren
                    </Button>
                    
                    {plan !== "free" && (
                      <Button 
                        onClick={openCustomerPortal} 
                        variant="outline" 
                        className="w-full gap-2"
                        disabled={isOpeningPortal}
                      >
                        {isOpeningPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                        Zahlungen & Rechnungen verwalten
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upgrade Options */}
              <Card>
                <CardHeader>
                  <CardTitle>Verfügbare Upgrades</CardTitle>
                  <CardDescription>
                    Erweitern Sie Ihren Plan für mehr Funktionen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {plan !== "basic" && plan !== "pro" && plan !== "premium" && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Basic</span>
                        <span className="text-lg font-bold">12€/Monat</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        150 Nachrichten/Monat, Rückruf-Funktion
                      </p>
                      <Button 
                        onClick={() => handleUpgrade("basic")} 
                        className="w-full"
                        disabled={isUpgrading !== null}
                      >
                        {isUpgrading === "basic" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Zu Basic wechseln
                      </Button>
                    </div>
                  )}
                  
                  {plan !== "pro" && plan !== "premium" && (
                    <div className="p-4 border-2 border-primary rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex items-center gap-2">
                          Pro <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">Beliebt</span>
                        </span>
                        <span className="text-lg font-bold">39€/Monat</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        750 Nachrichten/Monat, Eigenes Branding, Wissensdatenbank
                      </p>
                      <Button 
                        onClick={() => handleUpgrade("pro")} 
                        className="w-full"
                        disabled={isUpgrading !== null}
                      >
                        {isUpgrading === "pro" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Zu Pro wechseln
                      </Button>
                    </div>
                  )}
                  
                  {plan !== "premium" && (
                    <div className="p-4 border rounded-lg bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex items-center gap-2">
                          <Crown className="w-4 h-4 text-yellow-600" /> Premium
                        </span>
                        <span className="text-lg font-bold">89€/Monat</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        2000 Nachrichten/Monat, White-Label, API-Zugang
                      </p>
                      <Button 
                        onClick={() => handleUpgrade("premium")} 
                        className="w-full"
                        disabled={isUpgrading !== null}
                        variant="default"
                      >
                        {isUpgrading === "premium" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Zu Premium wechseln
                      </Button>
                    </div>
                  )}
                  
                  {plan === "premium" && (
                    <div className="text-center py-8">
                      <Crown className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
                      <h3 className="font-semibold mb-2">Sie nutzen bereits Premium!</h3>
                      <p className="text-muted-foreground">
                        Sie haben Zugang zu allen Funktionen.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Embed Tab */}
          <TabsContent value="embed">
            <Card>
              <CardHeader>
                <CardTitle>Widget einbetten</CardTitle>
                <CardDescription>
                  Kopieren Sie diesen Code und fügen Sie ihn vor dem schließenden &lt;/body&gt; Tag Ihrer Website ein.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2 gap-1"
                    onClick={() => copyToClipboard(embedCode)}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Kopiert!" : "Kopieren"}
                  </Button>
                </div>

                <div className="bg-primary/10 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Ihr API-Key:</h4>
                  <code className="text-sm bg-background px-2 py-1 rounded">
                    {apiKey}
                  </code>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Installationsanleitung:</h4>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>Kopieren Sie den obigen Code</li>
                    <li>Öffnen Sie den HTML-Code Ihrer Website</li>
                    <li>Fügen Sie den Code vor &lt;/body&gt; ein</li>
                    <li>Speichern und aktualisieren Sie Ihre Seite</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
